/* Academy (F22 · api-v14): cómo se usa la casa, contado por módulo y filtrado
   por lo que cada quien puede hacer.

   No hay una lista por rol escrita a mano: cada tutorial declara el permiso que
   enseña y la API lo cruza con la identidad. Por eso la directora de cuentas ve
   lo suyo, IT lo suyo, y el Admin lo ve todo — y el día que a alguien le
   conceden Finanzas, sus tutoriales aparecen solos. */
import { api, type Academy, type Tutorial, type TutorialRef } from "../api";
import { estado } from "../estado";
import { esc, toast } from "../ui";
import { sinInfo } from "./bloqueado";
import { ir, type Ruta } from "../router";
import type { Vista } from "./tipos";

/* A dónde lleva cada módulo. Sirve para dos cosas: abrir la pantalla desde el
   tutorial, y llevar a quien no tiene el módulo a la puerta donde se pide. */
const PANTALLA: Record<string, string> = {
  pm: "#/proyectos", timesheet: "#/timesheet", knowledge: "#/conocimiento", makai: "#/makai", finanzas: "#/finanzas",
  medios: "#/medios", aprobaciones: "#/aprobaciones", settings: "#/ajustes", compliance: "#/compliance",
  auditoria: "#/auditoria", empezar: "#/inicio",
};
const ICONO_OK = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12.5l5 5L20 6.5"/></svg>';
const RELOJ = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5.4l3.4 2"/></svg>';

const minutos = (n: number) => RELOJ + " " + n + " min";

function tarjeta(t: TutorialRef) {
  return '<div class="tut' + (t.completado ? " hecho" : "") + '" data-go="#/academy/' + esc(t.slug) + '">' +
    '<div class="check">' + (t.completado ? ICONO_OK : "") + "</div>" +
    '<div style="flex-grow:1;min-width:0"><div class="t">' + esc(t.titulo) + (t.esencial ? '<span class="tag esencial">Esencial</span>' : "") + "</div>" +
      '<div class="m">' + esc(t.resumen) + "</div></div>" +
    '<div class="min">' + minutos(t.minutos) + "</div></div>";
}

// ---------------------------------------------------------------- el índice
async function indice(): Promise<Vista> {
  const a: Academy = await api.academy();
  const p = a.progreso;
  const esenciales = a.tutoriales.filter((t) => t.esencial);
  const modulos = [...new Set(a.tutoriales.map((t) => t.modulo))];
  const pendiente = a.tutoriales.find((t) => t.esencial && !t.completado) ?? a.tutoriales.find((t) => !t.completado);
  const pctEsencial = p.esenciales ? Math.round((p.esencialesCompletados / p.esenciales) * 100) : 100;
  const pendientesEsenciales = esenciales.filter((t) => !t.completado);

  return {
    titulo: "Academy",
    sub: p.total + " tutoriales para " + esc(a.rol) + " · " + (p.completados === 1 ? "1 hecho" : p.completados + " hechos") +
      (p.minutos ? " · quedan " + p.minutos + " min de lectura" : " · no te queda ninguno"),
    acciones: pendiente ? '<button class="btn primario" data-go="#/academy/' + esc(pendiente.slug) + '">' + (p.completados ? "Seguir" : "Empezar") + ": " + esc(pendiente.titulo) + "</button>" : "",
    html:
      /* La ruta esencial enseña lo que FALTA, no la lista entera otra vez: los
         módulos de abajo ya son el índice completo. Cuando no falta nada, la
         tarjeta lo dice y se quita de en medio. */
      (esenciales.length
        ? '<div class="card" style="padding:16px"><div style="display:flex;align-items:baseline;gap:10px;flex-wrap:wrap">' +
            '<div style="font-size:15px;font-weight:640">Tu primera semana</div>' +
            '<span style="font-size:12px;color:var(--text3)">lo mínimo para no depender de nadie · ' + p.esencialesCompletados + " de " + p.esenciales + "</span></div>" +
            '<div class="barra" style="margin-top:10px"><div style="width:' + pctEsencial + '%;background:' + (pctEsencial === 100 ? "var(--ok)" : "var(--accent)") + '"></div></div>' +
            (pendientesEsenciales.length
              ? '<div class="tut-lista" style="margin-top:12px">' + pendientesEsenciales.slice(0, 5).map(tarjeta).join("") + "</div>" +
                (pendientesEsenciales.length > 5 ? '<div style="font-size:11.5px;color:var(--text3);margin-top:8px">y ' + (pendientesEsenciales.length - 5) + " más, por módulo, aquí abajo.</div>" : "")
              : '<div style="font-size:12.5px;color:var(--ok2);margin-top:10px">Ya los hiciste todos. Lo de abajo es el resto de la casa, cuando lo necesites.</div>') +
          "</div>"
        : "") +
      modulos.map((m) => {
        const ts = a.tutoriales.filter((t) => t.modulo === m);
        const hechos = ts.filter((t) => t.completado).length;
        return '<div class="card"><div class="cab"><div class="t">' + esc(ts[0]!.moduloNombre) + "</div>" +
          '<span style="font-size:12px;color:var(--text3)">' + hechos + " de " + ts.length + " · " + ts.reduce((s, t) => s + t.minutos, 0) + " min</span>" +
          (PANTALLA[m] ? '<button class="btn chico fantasma" data-go="' + PANTALLA[m] + '">Abrir el módulo</button>' : "") + "</div>" +
          '<div class="tut-lista" style="padding:12px 14px">' + ts.map(tarjeta).join("") + "</div></div>";
      }).join("") +
      /* Lo que existe y no te toca. No se esconde: saber que hay una puerta no
         es lo mismo que poder abrirla, y quien la necesite tiene que saber a
         dónde ir a pedirla. */
      (a.bloqueados.length
        ? '<div class="card"><div class="cab"><div class="t">Lo que hace la casa y todavía no ves</div>' +
            '<span style="font-size:12px;color:var(--text3)">' + a.bloqueados.reduce((s, b) => s + b.cuantos, 0) + " tutoriales de módulos que no tienes concedidos</span></div>" +
            a.bloqueados.map((b) => '<div class="fila"' + (PANTALLA[b.modulo] ? ' data-go="' + PANTALLA[b.modulo] + '"' : ' style="cursor:default"') + '>' +
              '<div style="flex-grow:1;min-width:0"><div class="t">' + esc(b.moduloNombre) + '</div><div class="m">' + b.cuantos + " tutorial" + (b.cuantos > 1 ? "es" : "") + " que aparecerán solos cuando tengas el módulo</div></div>" +
              (PANTALLA[b.modulo] ? '<span class="tag" style="color:var(--warnText);background:var(--warnBg)">Pedirlo</span>' : "") + "</div>").join("") +
            '<div style="padding:12px 16px;font-size:11.5px;color:var(--text3);border-top:1px solid var(--divider);line-height:1.55">Los módulos y los clientes se conceden por persona; lo que puedes hacer dentro lo decide tu rol. Entra al módulo con candado y la pantalla te dice cómo pedirlo.</div></div>'
        : ""),
  };
}

// ---------------------------------------------------------------- un tutorial
async function tutorial(slug: string): Promise<Vista> {
  let t: Tutorial;
  try { t = await api.tutorial(slug); } catch (e) {
    const err = e as { status?: number; mensaje?: string };
    if (err.status === 403) return {
      titulo: "Ese tutorial no es tuyo todavía", miga: ["Academy"],
      html: '<div class="aviso warn">' + esc(err.mensaje || "") + '</div><button class="btn" data-go="#/academy" style="margin-top:12px">Volver al Academy</button>',
    };
    if (err.status === 404) return sinInfo();
    throw e;
  }
  const p = t.progreso;
  return {
    titulo: t.titulo, miga: ["Academy", t.titulo],
    sub: t.moduloNombre + " · " + t.minutos + " min" + (t.esencial ? " · de tu primera semana" : "") + " · " + t.resumen,
    acciones: (PANTALLA[t.modulo] ? '<button class="btn" data-go="' + PANTALLA[t.modulo] + '">Abrir el módulo</button>' : "") +
      '<button class="btn ' + (t.completado ? "" : "primario") + '" id="ac-hecho">' + (t.completado ? "Marcar como pendiente" : "Ya lo hice") + "</button>",
    html:
      '<div class="dos-col"><div class="col-principal">' +
        '<div class="card" style="padding:16px 18px"><div class="eyebrow">Para qué existe</div>' +
          '<div style="font-size:13.5px;line-height:1.65;margin-top:8px;color:var(--text2)">' + esc(t.porque) + "</div></div>" +
        '<div class="card"><div class="cab"><div class="t">Cómo se hace</div><span style="font-size:12px;color:var(--text3)">' + t.pasos.length + " pasos</span></div>" +
          '<div class="pasos-tut">' + t.pasos.map((x, i) => '<div class="paso"><div class="n">' + (i + 1) + "</div>" +
            '<div class="txt">' + esc(x.t) + "</div>" +
            (x.ruta ? '<button class="btn chico" data-go="' + esc(x.ruta) + '">Abrir</button>' : "") + "</div>").join("") + "</div></div>" +
        '<div class="aviso warn"><b>La trampa:</b> ' + esc(t.trampa) + "</div>" +
        '<div class="aviso ok"><b>Sabrás que salió bien cuando:</b> ' + esc(t.listo) + "</div>" +
        '<div style="display:flex;gap:8px;flex-wrap:wrap">' +
          (t.anterior ? '<button class="btn" data-go="#/academy/' + esc(t.anterior.slug) + '">← ' + esc(t.anterior.titulo) + "</button>" : "") +
          (t.siguiente ? '<button class="btn primario" data-go="#/academy/' + esc(t.siguiente.slug) + '">' + esc(t.siguiente.titulo) + " →</button>" : "") +
        "</div>" +
      "</div>" +
      '<div class="col-lado" style="width:290px">' +
        '<div class="card" style="padding:15px 16px"><div class="eyebrow">Tu avance</div>' +
          '<div style="font-size:22px;font-weight:660;margin-top:6px">' + p.completados + " <span style=\"font-size:14px;color:var(--text3);font-weight:520\">de " + p.total + "</span></div>" +
          '<div class="barra" style="margin-top:8px"><div style="width:' + (p.total ? Math.round((p.completados / p.total) * 100) : 0) + '%;background:var(--accent)"></div></div>' +
          '<div style="font-size:11.5px;color:var(--text3);margin-top:8px;line-height:1.5">De tu primera semana llevas ' + p.esencialesCompletados + " de " + p.esenciales + ".</div>" +
          '<button class="btn" data-go="#/academy" style="width:100%;justify-content:center;margin-top:12px">Ver todos</button></div>' +
        '<div class="card" style="padding:15px 16px"><div class="eyebrow">Quién ve esto</div>' +
          '<div style="font-size:12.5px;color:var(--text2);margin-top:8px;line-height:1.6">Este tutorial le sale a quien puede <b>' + esc(requisitoHumano(t.requisito)) + "</b>. Si a alguien de tu equipo no le aparece, es que todavía no tiene ese permiso — y el tutorial aparecerá solo el día que se lo concedan.</div></div>" +
      "</div></div>",
    despues(root) {
      const b = root.querySelector<HTMLButtonElement>("#ac-hecho");
      if (b) b.onclick = async () => {
        b.disabled = true;
        try {
          const r = await api.marcarTutorial(t.slug, !t.completado);
          toast(r.completado ? "<b>Hecho.</b> " + (r.progreso.total - r.progreso.completados ? "Te quedan " + (r.progreso.total - r.progreso.completados) + "." : "No te queda ninguno.") : "Marcado como pendiente.");
          ir("#/academy/" + t.slug);
        } catch (e) { b.disabled = false; toast("<b>No se pudo guardar.</b> " + esc((e as Error).message)); }
      };
    },
  };
}

/** El permiso, dicho como lo diría una persona. */
function requisitoHumano(r: string): string {
  if (r === "todos") return "entrar a la app";
  if (r === "firma") return "firmar contenido de AI";
  if (r === "auditoria") return "leer el registro de auditoría";
  const [ambito, accion = "ver"] = r.split(":");
  const verbo: Record<string, string> = { ver: "entrar a", crear: "crear en", editar: "editar en", aprobar: "aprobar en", eliminar: "borrar en", exportar: "exportar de" };
  const modulo: Record<string, string> = { pm: "Proyectos", timesheet: "Timesheet", knowledge: "Conocimiento", makai: estado.tenant?.nombreChat || "Makai", finanzas: "Finanzas", settings: "Ajustes", compliance: "Compliance" };
  if (ambito === "dinero") return (verbo[accion] ?? accion) + " el dinero (permiso propio, no módulo)";
  return (verbo[accion] ?? accion) + " " + (modulo[ambito!] ?? ambito);
}

export async function academy(r: Ruta): Promise<Vista> {
  return r.a ? tutorial(r.a) : indice();
}
